import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarRange,
  Clock,
  Truck,
  User,
  MapPin,
  Search,
  Filter,
  Plus,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Navigation,
  UserCheck,
  Printer,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  DollarSign,
  FileText,
  X,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  Layers,
  ListFilter,
  ArrowRight,
  ShieldAlert,
  Maximize2,
  Minimize2,
  ExternalLink,
} from 'lucide-react';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { VisualizarOSModal } from '../../pages/VisualizarOSModal';
import { CadastroOSModal } from '../../pages/CadastroOSModal';
import { CriadorRoteiroModal } from '../CriadorRoteiroModal';
import { TrocarMotoristaVeiculoModal } from '../ordemServico/TrocarMotoristaVeiculoModal';
import { isPessoaAtiva } from '../../services/pessoaService';
import { veiculoService } from '../../services/veiculoService';

export type AgendaViewMode = 'mes' | 'semana' | 'dia' | 'motorista' | 'lista';

interface FreteAgendaItem {
  id: number;
  pedido_id?: number;
  data_atendimento: string; // YYYY-MM-DD
  horario_inicio?: string;
  horario_final?: string;
  hora_total?: string;
  retirada?: string;
  entrega?: string;
  descricao?: string;
  ocorrencias?: string;
  total_os?: number;
  repasse?: number;
  valor_total_repasses?: number;
  valor_liquido?: number;
  status_id?: number;
  status_nome: string;
  status_cor: string;
  tipo_status: 'aberto' | 'andamento' | 'finalizado' | 'outro';
  
  // Relações
  cliente_id?: number;
  cliente_nome: string;
  veiculos_id?: string;
  veiculo_placa: string;
  veiculo_modelo: string;
  tipo_veiculo_nome: string;
  motorista_id?: number;
  motorista_nome: string;
  quantidade_ajudante?: number;
}

const DIAS_SEMANA_ABREV = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DIAS_SEMANA_EXTENSO = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
const MESES_ANO = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function formatBRL(v: number = 0) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function getStatusBadgeStyle(status_cor?: string, tipo?: string) {
  if (status_cor && status_cor.startsWith('#')) {
    return {
      backgroundColor: `${status_cor}20`,
      color: status_cor,
      borderColor: `${status_cor}40`,
    };
  }
  switch (tipo) {
    case 'aberto':
      return { backgroundColor: '#fef3c7', color: '#b45309', borderColor: '#fde68a' };
    case 'andamento':
      return { backgroundColor: '#dbeafe', color: '#1d4ed8', borderColor: '#bfdbfe' };
    case 'finalizado':
      return { backgroundColor: '#d1fae5', color: '#047857', borderColor: '#a7f3d0' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569', borderColor: '#e2e8f0' };
  }
}

export function AgendaFretesPanel() {
  const { hasPermission } = usePermissionsContext();
  const canViewPrices = hasPermission('view_order_prices') || hasPermission('view_financial');
  const canViewRepasse = hasPermission('view_order_repasse') || hasPermission('view_financial');
  const canCreateOS = hasPermission('create_orders');
  const canManageRoutes = hasPermission('manage_routes') || hasPermission('create_routes');

  // Estados principais
  const [viewMode, setViewMode] = useState<AgendaViewMode>('mes');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [fretes, setFretes] = useState<FreteAgendaItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMotoristaFilter, setSelectedMotoristaFilter] = useState<string>('todos');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('todos');
  const [selectedClienteFilter, setSelectedClienteFilter] = useState<string>('todos');
  const [startDateFilter, setStartDateFilter] = useState<string>('');
  const [endDateFilter, setEndDateFilter] = useState<string>('');
  const [showFiltersBar, setShowFiltersBar] = useState(false);

  // Listas de apoio para filtros
  const [motoristasList, setMotoristasList] = useState<{ id: number; nome: string }[]>([]);
  const [clientesList, setClientesList] = useState<{ id: number; nome: string }[]>([]);
  const [statusList, setStatusList] = useState<{ id: number; status: string; cor: string }[]>([]);
  const [veiculosList, setVeiculosList] = useState<any[]>([]);

  // Modais e visualização expandida
  const [monthViewDensity, setMonthViewDensity] = useState<'compacto' | 'expandido'>('expandido');
  const [dayDetailsModal, setDayDetailsModal] = useState<{ dateStr: string; dayNumber: number; fretes: FreteAgendaItem[] } | null>(null);
  const [viewOSId, setViewOSId] = useState<number | null>(null);
  const [isCadastroOpen, setIsCadastroOpen] = useState(false);
  const [cadastroDate, setCadastroDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [isRoteiroOpen, setIsRoteiroOpen] = useState(false);
  const [roteiroDate, setRoteiroDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [isTrocarMotoristaOpen, setIsTrocarMotoristaOpen] = useState(false);
  const [veiculoParaTroca, setVeiculoParaTroca] = useState<any>(null);

  // Carregar dados de fretes e relacionamentos
  const carregarDadosAgenda = async () => {
    try {
      setLoading(true);

      // 1. Carregar tabelas auxiliares
      const [
        { data: statusData },
        { data: pessoasData },
        { data: veiculosData },
        { data: pedidosData },
        { data: tiposVeiculoData },
        { data: modelosData },
      ] = await Promise.all([
        supabase.from('status').select('id, status, cor'),
        supabase.from('pessoa').select('id, nome, ativo, data_desativacao, deleted_at, situacao_cadastral').is('deleted_at', null),
        // Via veiculoService.getAll(): traz as colunas reais de `veiculos` e já
        // mescla `ativo`/`status_frota` (guardados em system_preference) - a
        // consulta direta na tabela não tem esses campos, então um veículo
        // desativado nunca era filtrado no modal de Trocar Motorista/Veículo.
        veiculoService.getAll().then((data) => ({ data })),
        supabase.from('pedido').select('id, pessoa_id'),
        supabase.from('tipo_veiculo').select('id, tipo_veiculo'),
        supabase.from('veiculo_modelo').select('id, modelo'),
      ]);

      const statusMap = new Map<number, { status: string; cor: string }>();
      (statusData || []).forEach(s => statusMap.set(Number(s.id), { status: s.status, cor: s.cor }));
      setStatusList(statusData || []);

      const pessoaMap = new Map<number, string>();
      const motoristasUnicos: { id: number; nome: string }[] = [];
      const clientesUnicos: { id: number; nome: string }[] = [];

      (pessoasData || []).forEach(p => {
        pessoaMap.set(Number(p.id), p.nome);
        // Pessoa inativa não deve aparecer para ser selecionada em novas
        // atribuições (troca de motorista, filtro de cliente etc.) - o nome
        // ainda é usado para exibir quem já está vinculado (via pessoaMap).
        if (!isPessoaAtiva(p)) return;
        motoristasUnicos.push({ id: p.id, nome: p.nome });
        clientesUnicos.push({ id: p.id, nome: p.nome });
      });

      setMotoristasList(motoristasUnicos.sort((a, b) => a.nome.localeCompare(b.nome)));
      setClientesList(clientesUnicos.sort((a, b) => a.nome.localeCompare(b.nome)));

      const tipoVeiculoMap = new Map<number, string>();
      (tiposVeiculoData || []).forEach(t => tipoVeiculoMap.set(Number(t.id), t.tipo_veiculo));

      const modeloMap = new Map<number, string>();
      (modelosData || []).forEach(m => modeloMap.set(Number(m.id), m.modelo));

      const veiculoMap = new Map<string, any>();
      (veiculosData || []).forEach(v => {
        veiculoMap.set(String(v.id), {
          ...v,
          motorista_nome: v.motorista_id ? pessoaMap.get(Number(v.motorista_id)) : '',
          tipo_nome: v.tipo_veiculo ? tipoVeiculoMap.get(Number(v.tipo_veiculo)) : '',
          modelo_nome: v.veiculo_modelo_id ? modeloMap.get(Number(v.veiculo_modelo_id)) : '',
        });
      });
      // veiculosList alimenta o seletor do modal "Trocar Motorista/Veículo" -
      // veículo desativado ou em manutenção não deve poder ser escolhido ali.
      // (veiculoMap, acima, continua com todos - é usado para exibir o
      // veículo de OSs já existentes, mesmo que hoje esteja desativado.)
      setVeiculosList(
        (veiculosData || []).filter(
          (v: any) => v.ativo !== false && v.status_frota !== 'em_manutencao' && v.status_frota !== 'desativado'
        )
      );

      const pedidoClienteMap = new Map<number, number>();
      (pedidosData || []).forEach(p => {
        if (p.pessoa_id) pedidoClienteMap.set(Number(p.id), Number(p.pessoa_id));
      });

      // 2. Carregar todas as ordens de serviço
      let allOS: any[] = [];
      let from = 0;
      const step = 1000;
      while (true) {
        const { data, error } = await supabase
          .from('ordem_de_servico')
          .select('*')
          .order('data_atendimento', { ascending: true })
          .range(from, from + step - 1);

        if (error) {
          console.warn('[Agenda] erro ao paginar OS:', error.message);
          break;
        }
        if (!data || data.length === 0) break;
        allOS = allOS.concat(data);
        if (data.length < step) break;
        from += step;
      }

      // 3. Montar itens da agenda
      const itensFormatados: FreteAgendaItem[] = allOS.map((os) => {
        const sId = Number(os.status_id);
        const sMeta = statusMap.get(sId);
        const sNome = sMeta?.status || `Status #${sId || '0'}`;
        const sCor = sMeta?.cor || '#64748b';

        let tipoStatus: FreteAgendaItem['tipo_status'] = 'outro';
        const sNomeLow = sNome.toLowerCase();
        if (
          sNomeLow.includes('conclu') || 
          sNomeLow.includes('final') || 
          sNomeLow.includes('faturad') || 
          sNomeLow.includes('entregue') ||
          sNomeLow.includes('encerrad') ||
          sNomeLow.includes('baixad') ||
          sNomeLow.includes('fechad') ||
          sNomeLow.includes('resolvid') ||
          !!os.assinatura
        ) {
          tipoStatus = 'finalizado';
        } else if (sNomeLow.includes('abert') || sNomeLow.includes('pendente') || sNomeLow.includes('agendad') || sNomeLow.includes('solicitad')) {
          tipoStatus = 'aberto';
        } else {
          tipoStatus = 'andamento';
        }

        const clienteId = os.pedido_id ? pedidoClienteMap.get(Number(os.pedido_id)) : undefined;
        const clienteNome = clienteId ? (pessoaMap.get(clienteId) || `Cliente #${clienteId}`) : 'Cliente Geral';

        const veiculo = os.veiculos_id ? veiculoMap.get(String(os.veiculos_id)) : null;
        const motoristaId = os.agregado ? Number(os.agregado) : (veiculo?.motorista_id ? Number(veiculo.motorista_id) : undefined);
        const motoristaNome = motoristaId ? (pessoaMap.get(motoristaId) || `Motorista #${motoristaId}`) : (veiculo?.motorista_nome || 'Sem motorista');

        const tipoVeiculoNome = os.tipo_veiculo_id
          ? (tipoVeiculoMap.get(Number(os.tipo_veiculo_id)) || 'Veículo Padrão')
          : (veiculo?.tipo_nome || 'Veículo Padrão');

        // Formata data ISO YYYY-MM-DD
        let dataAtend = os.data_atendimento;
        if (!dataAtend && os.created_at) {
          dataAtend = os.created_at.split('T')[0];
        }
        if (!dataAtend) {
          dataAtend = new Date().toISOString().split('T')[0];
        }

        return {
          id: os.id,
          pedido_id: os.pedido_id,
          data_atendimento: dataAtend,
          horario_inicio: os.horario_inicio || '',
          horario_final: os.horario_final || '',
          hora_total: os.hora_total || '',
          retirada: os.retirada || '',
          entrega: os.entrega || '',
          descricao: os.descricao || '',
          ocorrencias: os.ocorrencias || '',
          total_os: Number(os.total_os) || 0,
          repasse: Number(os.repasse) || 0,
          valor_total_repasses: Number(os.valor_total_repasses) || 0,
          valor_liquido: Number(os.valor_liquido) || 0,
          status_id: sId,
          status_nome: sNome,
          status_cor: sCor,
          tipo_status: tipoStatus,
          cliente_id: clienteId,
          cliente_nome: clienteNome,
          veiculos_id: os.veiculos_id,
          veiculo_placa: veiculo?.placa || os.placa || '',
          veiculo_modelo: veiculo?.modelo_nome || os.modelo || '',
          tipo_veiculo_nome: tipoVeiculoNome,
          motorista_id: motoristaId,
          motorista_nome: motoristaNome,
          quantidade_ajudante: Number(os.quantidade_ajudante) || 0,
        };
      });

      setFretes(itensFormatados);
    } catch (err) {
      console.error('Erro ao carregar dados da agenda de fretes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDadosAgenda();

    // Inscrição em tempo real para sincronização instantânea
    const channel = supabase
      .channel('agenda-fretes-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ordem_de_servico' },
        () => {
          carregarDadosAgenda();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Listas limpas e organizadas para os filtros suspensos (apenas clientes e motoristas reais que possuem fretes)
  const clientesOptions = useMemo(() => {
    const map = new Map<string, { id: number | string; nome: string; count: number }>();
    fretes.forEach((f) => {
      if (f.cliente_id && f.cliente_nome) {
        const key = String(f.cliente_id);
        const curr = map.get(key) || { id: f.cliente_id, nome: f.cliente_nome, count: 0 };
        curr.count++;
        map.set(key, curr);
      } else if (f.cliente_nome && f.cliente_nome !== 'Cliente Geral') {
        const key = f.cliente_nome;
        const curr = map.get(key) || { id: key, nome: f.cliente_nome, count: 0 };
        curr.count++;
        map.set(key, curr);
      }
    });
    return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [fretes]);

  const motoristasOptions = useMemo(() => {
    const map = new Map<string, { id: number | string; nome: string; count: number }>();
    fretes.forEach((f) => {
      if (f.motorista_id && f.motorista_nome && f.motorista_nome !== 'Sem motorista') {
        const key = String(f.motorista_id);
        const curr = map.get(key) || { id: f.motorista_id, nome: f.motorista_nome, count: 0 };
        curr.count++;
        map.set(key, curr);
      }
    });
    return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [fretes]);

  const statusOptions = useMemo(() => {
    return statusList;
  }, [statusList]);

  // Função auxiliar de atalhos rápidos para filtros de data a data
  const aplicarPresetData = (tipo: 'hoje' | 'esta_semana' | 'este_mes' | 'proximos_7' | 'proximos_30' | 'limpar') => {
    const hoje = new Date();
    const hojeStr = hoje.toISOString().split('T')[0];

    if (tipo === 'limpar') {
      setStartDateFilter('');
      setEndDateFilter('');
      return;
    }

    if (tipo === 'hoje') {
      setStartDateFilter(hojeStr);
      setEndDateFilter(hojeStr);
      setSelectedDate(hojeStr);
      return;
    }

    if (tipo === 'esta_semana') {
      const d = new Date(hoje);
      const day = d.getDay();
      const domingo = new Date(d);
      domingo.setDate(d.getDate() - day);
      const sabado = new Date(domingo);
      sabado.setDate(domingo.getDate() + 6);
      setStartDateFilter(domingo.toISOString().split('T')[0]);
      setEndDateFilter(sabado.toISOString().split('T')[0]);
      return;
    }

    if (tipo === 'este_mes') {
      const ano = hoje.getFullYear();
      const mes = hoje.getMonth();
      const inicio = `${ano}-${String(mes + 1).padStart(2, '0')}-01`;
      const ultimoDia = new Date(ano, mes + 1, 0).getDate();
      const fim = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
      setStartDateFilter(inicio);
      setEndDateFilter(fim);
      return;
    }

    if (tipo === 'proximos_7') {
      const fim = new Date(hoje);
      fim.setDate(hoje.getDate() + 7);
      setStartDateFilter(hojeStr);
      setEndDateFilter(fim.toISOString().split('T')[0]);
      return;
    }

    if (tipo === 'proximos_30') {
      const fim = new Date(hoje);
      fim.setDate(hoje.getDate() + 30);
      setStartDateFilter(hojeStr);
      setEndDateFilter(fim.toISOString().split('T')[0]);
      return;
    }
  };

  // Filtragem dos fretes
  const fretesFiltrados = useMemo(() => {
    return fretes.filter((f) => {
      // Filtro por termo de busca
      if (searchTerm.trim()) {
        const termo = searchTerm.toLowerCase().trim();
        const matchOS = String(f.id).includes(termo);
        const matchCliente = f.cliente_nome.toLowerCase().includes(termo);
        const matchMotorista = f.motorista_nome.toLowerCase().includes(termo);
        const matchPlaca = f.veiculo_placa.toLowerCase().includes(termo);
        const matchRetirada = f.retirada?.toLowerCase().includes(termo);
        const matchEntrega = f.entrega?.toLowerCase().includes(termo);
        if (!matchOS && !matchCliente && !matchMotorista && !matchPlaca && !matchRetirada && !matchEntrega) {
          return false;
        }
      }

      // Filtro por data inicial (De)
      if (startDateFilter) {
        if (!f.data_atendimento || f.data_atendimento < startDateFilter) return false;
      }

      // Filtro por data final (Até)
      if (endDateFilter) {
        if (!f.data_atendimento || f.data_atendimento > endDateFilter) return false;
      }

      // Filtro por motorista
      if (selectedMotoristaFilter !== 'todos') {
        if (selectedMotoristaFilter === 'sem_motorista') {
          if (f.motorista_id) return false;
        } else {
          if (String(f.motorista_id) !== String(selectedMotoristaFilter)) return false;
        }
      }

      // Filtro por status
      if (selectedStatusFilter !== 'todos') {
        if (String(f.status_id) !== String(selectedStatusFilter)) return false;
      }

      // Filtro por cliente
      if (selectedClienteFilter !== 'todos') {
        if (f.cliente_id && String(f.cliente_id) === String(selectedClienteFilter)) {
          // match
        } else if (f.cliente_nome === selectedClienteFilter) {
          // match
        } else {
          return false;
        }
      }

      return true;
    });
  }, [fretes, searchTerm, startDateFilter, endDateFilter, selectedMotoristaFilter, selectedStatusFilter, selectedClienteFilter]);

  // Mapa de fretes agrupados por data (YYYY-MM-DD)
  const fretesPorData = useMemo(() => {
    const mapa = new Map<string, FreteAgendaItem[]>();
    fretesFiltrados.forEach((f) => {
      const d = f.data_atendimento;
      if (!mapa.has(d)) mapa.set(d, []);
      mapa.get(d)!.push(f);
    });
    return mapa;
  }, [fretesFiltrados]);

  // Fretes do dia selecionado (para detalhamento lateral/inferior)
  const fretesDoDiaSelecionado = useMemo(() => {
    const lista = fretesPorData.get(selectedDate) || [];
    return [...lista].sort((a, b) => (a.horario_inicio || '00:00').localeCompare(b.horario_inicio || '00:00'));
  }, [fretesPorData, selectedDate]);

  // Funções de Navegação de Data
  const handlePrev = () => {
    const nova = new Date(currentDate);
    if (viewMode === 'mes') {
      nova.setMonth(nova.getMonth() - 1);
    } else if (viewMode === 'semana') {
      nova.setDate(nova.getDate() - 7);
    } else {
      nova.setDate(nova.getDate() - 1);
    }
    setCurrentDate(nova);
    setSelectedDate(nova.toISOString().split('T')[0]);
  };

  const handleNext = () => {
    const nova = new Date(currentDate);
    if (viewMode === 'mes') {
      nova.setMonth(nova.getMonth() + 1);
    } else if (viewMode === 'semana') {
      nova.setDate(nova.getDate() + 7);
    } else {
      nova.setDate(nova.getDate() + 1);
    }
    setCurrentDate(nova);
    setSelectedDate(nova.toISOString().split('T')[0]);
  };

  const handleHoje = () => {
    const hoje = new Date();
    setCurrentDate(hoje);
    setSelectedDate(hoje.toISOString().split('T')[0]);
  };

  // Gerador da grade de dias do mês atual
  const diasGradeMes = useMemo(() => {
    const ano = currentDate.getFullYear();
    const mes = currentDate.getMonth();

    const primeiroDiaMes = new Date(ano, mes, 1);
    const ultimoDiaMes = new Date(ano, mes + 1, 0);

    const diaSemanaInicio = primeiroDiaMes.getDay(); // 0 = Dom
    const totalDiasMes = ultimoDiaMes.getDate();

    // Dias do mês anterior para preencher a primeira semana
    const diasMesAnterior = new Date(ano, mes, 0).getDate();

    const grid: {
      dateStr: string;
      dayNumber: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      isSelected: boolean;
      fretes: FreteAgendaItem[];
    }[] = [];

    const hojeStr = new Date().toISOString().split('T')[0];

    // Preenche dias do mês anterior
    for (let i = diaSemanaInicio - 1; i >= 0; i--) {
      const d = diasMesAnterior - i;
      const prevDate = new Date(ano, mes - 1, d);
      const dateStr = prevDate.toISOString().split('T')[0];
      grid.push({
        dateStr,
        dayNumber: d,
        isCurrentMonth: false,
        isToday: dateStr === hojeStr,
        isSelected: dateStr === selectedDate,
        fretes: fretesPorData.get(dateStr) || [],
      });
    }

    // Preenche dias do mês corrente
    for (let d = 1; d <= totalDiasMes; d++) {
      const currDate = new Date(ano, mes, d);
      const dateStr = currDate.toISOString().split('T')[0];
      grid.push({
        dateStr,
        dayNumber: d,
        isCurrentMonth: true,
        isToday: dateStr === hojeStr,
        isSelected: dateStr === selectedDate,
        fretes: fretesPorData.get(dateStr) || [],
      });
    }

    // Preenche dias do próximo mês para completar 35 ou 42 células
    const totalRestante = 42 - grid.length;
    for (let d = 1; d <= totalRestante; d++) {
      const nextDate = new Date(ano, mes + 1, d);
      const dateStr = nextDate.toISOString().split('T')[0];
      grid.push({
        dateStr,
        dayNumber: d,
        isCurrentMonth: false,
        isToday: dateStr === hojeStr,
        isSelected: dateStr === selectedDate,
        fretes: fretesPorData.get(dateStr) || [],
      });
    }

    return grid;
  }, [currentDate, selectedDate, fretesPorData]);

  // Gerador dos dias da semana atual
  const diasSemanaAtual = useMemo(() => {
    const curr = new Date(currentDate);
    const dayOfWeek = curr.getDay(); // 0 = Dom
    // Inicia no Domingo
    const domingo = new Date(curr);
    domingo.setDate(curr.getDate() - dayOfWeek);

    const hojeStr = new Date().toISOString().split('T')[0];
    const semana: {
      dateStr: string;
      diaSemana: string;
      diaSemanaAbrev: string;
      dayNumber: number;
      isToday: boolean;
      isSelected: boolean;
      fretes: FreteAgendaItem[];
    }[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(domingo);
      d.setDate(domingo.getDate() + i);
      const dateStr = d.toISOString().split('T')[0];
      semana.push({
        dateStr,
        diaSemana: DIAS_SEMANA_EXTENSO[i],
        diaSemanaAbrev: DIAS_SEMANA_ABREV[i],
        dayNumber: d.getDate(),
        isToday: dateStr === hojeStr,
        isSelected: dateStr === selectedDate,
        fretes: fretesPorData.get(dateStr) || [],
      });
    }

    return semana;
  }, [currentDate, selectedDate, fretesPorData]);

  // Fretes e Rótulo do Período Ativo (Dinâmico: Intervalo Personalizado, Mês, Semana, Dia ou Geral)
  const { fretesPeriodo, periodoLabel, periodoTipo } = useMemo(() => {
    // Se o usuário definiu um filtro explícito de data a data
    if (startDateFilter || endDateFilter) {
      let label = '';
      if (startDateFilter && endDateFilter) {
        if (startDateFilter === endDateFilter) {
          const d = new Date(startDateFilter + 'T12:00:00');
          label = isNaN(d.getTime()) ? startDateFilter : d.toLocaleDateString('pt-BR');
        } else {
          const dIni = new Date(startDateFilter + 'T12:00:00');
          const dFim = new Date(endDateFilter + 'T12:00:00');
          const iniStr = isNaN(dIni.getTime()) ? startDateFilter : dIni.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const fimStr = isNaN(dFim.getTime()) ? endDateFilter : dFim.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          label = `${iniStr} a ${fimStr}`;
        }
      } else if (startDateFilter) {
        const dIni = new Date(startDateFilter + 'T12:00:00');
        const iniStr = isNaN(dIni.getTime()) ? startDateFilter : dIni.toLocaleDateString('pt-BR');
        label = `A partir de ${iniStr}`;
      } else {
        const dFim = new Date(endDateFilter + 'T12:00:00');
        const fimStr = isNaN(dFim.getTime()) ? endDateFilter : dFim.toLocaleDateString('pt-BR');
        label = `Até ${fimStr}`;
      }

      return {
        fretesPeriodo: fretesFiltrados,
        periodoLabel: label,
        periodoTipo: 'Intervalo',
      };
    }

    if (viewMode === 'mes') {
      const ano = currentDate.getFullYear();
      const mes = currentDate.getMonth();
      const mesStr = `${ano}-${String(mes + 1).padStart(2, '0')}`;
      const lista = fretesFiltrados.filter((f) => f.data_atendimento && f.data_atendimento.startsWith(mesStr));
      return {
        fretesPeriodo: lista,
        periodoLabel: `${MESES_ANO[mes]} de ${ano}`,
        periodoTipo: 'Mês',
      };
    }

    if (viewMode === 'semana') {
      const startStr = diasSemanaAtual[0]?.dateStr || '';
      const endStr = diasSemanaAtual[6]?.dateStr || '';
      const lista = fretesFiltrados.filter((f) => f.data_atendimento >= startStr && f.data_atendimento <= endStr);
      const inicioFormat = diasSemanaAtual[0]
        ? `${String(diasSemanaAtual[0].dayNumber).padStart(2, '0')}/${diasSemanaAtual[0].dateStr.slice(5, 7)}`
        : '';
      const fimFormat = diasSemanaAtual[6]
        ? `${String(diasSemanaAtual[6].dayNumber).padStart(2, '0')}/${diasSemanaAtual[6].dateStr.slice(5, 7)}`
        : '';
      return {
        fretesPeriodo: lista,
        periodoLabel: `${inicioFormat} a ${fimFormat}`,
        periodoTipo: 'Semana',
      };
    }

    if (viewMode === 'dia' || viewMode === 'motorista') {
      const lista = fretesFiltrados.filter((f) => f.data_atendimento === selectedDate);
      const d = new Date(selectedDate + 'T12:00:00');
      const diaFormat = isNaN(d.getTime())
        ? selectedDate
        : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      return {
        fretesPeriodo: lista,
        periodoLabel: `${diaFormat}`,
        periodoTipo: 'Dia',
      };
    }

    // viewMode === 'lista'
    return {
      fretesPeriodo: fretesFiltrados,
      periodoLabel: 'Geral',
      periodoTipo: 'Lista',
    };
  }, [startDateFilter, endDateFilter, viewMode, currentDate, selectedDate, fretesFiltrados, diasSemanaAtual]);

  // KPIs e Resumo do Período Visível (Dinâmico para Mês, Semana, Dia ou Geral)
  const kpisPeriodo = useMemo(() => {
    let total = 0;
    let abertos = 0;
    let andamento = 0;
    let finalizados = 0;
    let faturamento = 0;
    const motoristasSet = new Set<number>();

    fretesPeriodo.forEach((f) => {
      total++;
      if (f.tipo_status === 'aberto') abertos++;
      else if (f.tipo_status === 'andamento') andamento++;
      else if (f.tipo_status === 'finalizado') finalizados++;

      faturamento += f.total_os || 0;
      if (f.motorista_id) motoristasSet.add(f.motorista_id);
    });

    return {
      total,
      abertos,
      andamento,
      finalizados,
      faturamento,
      motoristasAtivos: motoristasSet.size,
    };
  }, [fretesPeriodo]);

  // Agrupamento por Motorista para a visão "Por Motorista"
  const motoristasSwimlanes = useMemo(() => {
    const fretesDoPeriodo = fretesFiltrados.filter(f => f.data_atendimento === selectedDate);
    
    // Lista de motoristas que têm frete no dia
    const mapMotoristas = new Map<string, { motoristaNome: string; fretes: FreteAgendaItem[] }>();
    
    // Entrada para fretes sem motorista
    mapMotoristas.set('sem_motorista', {
      motoristaNome: '⚠️ Sem Motorista Vinculado',
      fretes: [],
    });

    fretesDoPeriodo.forEach(f => {
      const key = f.motorista_id ? String(f.motorista_id) : 'sem_motorista';
      if (!mapMotoristas.has(key)) {
        mapMotoristas.set(key, {
          motoristaNome: f.motorista_nome || `Motorista #${key}`,
          fretes: [],
        });
      }
      mapMotoristas.get(key)!.fretes.push(f);
    });

    return Array.from(mapMotoristas.entries())
      .filter(([key, data]) => key === 'sem_motorista' ? data.fretes.length > 0 : true)
      .map(([key, data]) => ({
        key,
        nome: data.motoristaNome,
        fretes: data.fretes.sort((a, b) => (a.horario_inicio || '00:00').localeCompare(b.horario_inicio || '00:00')),
      }));
  }, [fretesFiltrados, selectedDate]);

  const handleOpenNovaOS = (dataPadrao?: string) => {
    setCadastroDate(dataPadrao || selectedDate || new Date().toISOString().split('T')[0]);
    setIsCadastroOpen(true);
  };

  const handleOpenRoteirizador = (dataPadrao?: string) => {
    setRoteiroDate(dataPadrao || selectedDate || new Date().toISOString().split('T')[0]);
    setIsRoteiroOpen(true);
  };

  const handleImprimirAgenda = () => {
    window.print();
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Header do Painel com Título e Ações Rápidas */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm print:hidden">
        <div className="flex items-center gap-3.5">
          <div className="p-3 bg-red-600 text-white rounded-2xl shadow-md shadow-red-600/20">
            <CalendarDays className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-black text-slate-900 tracking-tight uppercase">
                Agenda & Planner de Fretes
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-slate-900 text-amber-400 border border-slate-800">
                Torre de Despacho
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Planejamento visual, escala de motoristas e cronograma de viagens em tempo real.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canCreateOS && (
            <button
              onClick={() => handleOpenNovaOS()}
              className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-md shadow-red-600/20 transition-all flex items-center gap-1.5 cursor-pointer"
              title="Criar novo frete / ordem de serviço"
            >
              <Plus className="w-4 h-4" />
              <span>Novo Frete (OS)</span>
            </button>
          )}

          {canManageRoutes && (
            <button
              onClick={() => handleOpenRoteirizador()}
              className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
              title="Abrir Criador e Sequenciador de Roteiros"
            >
              <Navigation className="w-4 h-4 text-amber-400" />
              <span>Roteirizar Dia</span>
            </button>
          )}

          <button
            onClick={handleImprimirAgenda}
            className="p-2.5 text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
            title="Imprimir visualização da agenda"
          >
            <Printer className="w-4 h-4" />
          </button>

          <button
            onClick={carregarDadosAgenda}
            className="p-2.5 text-slate-600 hover:text-red-600 bg-slate-100 hover:bg-red-50 rounded-xl transition-all"
            title="Atualizar dados da agenda"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* 2. Barra de KPIs do Período Dinâmico */}
      <div className="space-y-2 print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-black text-slate-700 tracking-wider uppercase flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              Indicadores do Período:
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-200 shadow-2xs">
              {periodoTipo}: {periodoLabel}
            </span>
          </div>
          <span className="text-[11px] text-slate-400 font-medium">
            Métricas atualizadas dinamicamente para a visão atual
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between hover:border-slate-300 transition">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
              <FileText className="w-3.5 h-3.5 text-slate-500" /> Total Fretes
            </span>
            <span className="text-xl font-black text-slate-900 font-mono mt-1">
              {kpisPeriodo.total}
            </span>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between hover:border-amber-300 transition">
            <span className="text-[10px] font-bold text-amber-600 uppercase tracking-wider flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-amber-500" /> Pendentes/Abertos
            </span>
            <span className="text-xl font-black text-amber-700 font-mono mt-1">
              {kpisPeriodo.abertos}
            </span>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between hover:border-blue-300 transition">
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider flex items-center gap-1">
              <Truck className="w-3.5 h-3.5 text-blue-500" /> Em Rota / Trânsito
            </span>
            <span className="text-xl font-black text-blue-700 font-mono mt-1">
              {kpisPeriodo.andamento}
            </span>
          </div>

          <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between hover:border-emerald-300 transition">
            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Finalizados
            </span>
            <span className="text-xl font-black text-emerald-700 font-mono mt-1">
              {kpisPeriodo.finalizados}
            </span>
          </div>

          {canViewPrices ? (
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between hover:border-emerald-300 transition">
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5 text-emerald-500" /> Previsto Total
              </span>
              <span className="text-base font-black text-emerald-700 font-mono mt-1 truncate" title={formatBRL(kpisPeriodo.faturamento)}>
                {formatBRL(kpisPeriodo.faturamento)}
              </span>
            </div>
          ) : (
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Status Geral
              </span>
              <span className="text-sm font-bold text-slate-700 mt-1">
                Ativo
              </span>
            </div>
          )}

          <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs flex flex-col justify-between hover:border-purple-300 transition">
            <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-purple-500" /> Motoristas Ativos
            </span>
            <span className="text-xl font-black text-purple-700 font-mono mt-1">
              {kpisPeriodo.motoristasAtivos}
            </span>
          </div>
        </div>
      </div>

      {/* 3. Controles de Visualização, Data e Filtros */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-4 print:hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Controles de Navegação de Data */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleHoje}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-lg transition"
            >
              Hoje
            </button>
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5 border border-slate-200">
              <button
                onClick={handlePrev}
                className="p-1 text-slate-600 hover:text-slate-900 rounded-md hover:bg-white transition"
                title="Período anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNext}
                className="p-1 text-slate-600 hover:text-slate-900 rounded-md hover:bg-white transition"
                title="Próximo período"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Label da Data/Mês */}
            <div className="text-base font-black text-slate-900 tracking-tight capitalize pl-1">
              {viewMode === 'mes' && `${MESES_ANO[currentDate.getMonth()]} de ${currentDate.getFullYear()}`}
              {viewMode === 'semana' && (
                <>
                  Semana {diasSemanaAtual[0]?.dayNumber} {MESES_ANO[new Date(diasSemanaAtual[0]?.dateStr).getMonth()]} - {diasSemanaAtual[6]?.dayNumber} {MESES_ANO[new Date(diasSemanaAtual[6]?.dateStr).getMonth()]}
                </>
              )}
              {viewMode === 'dia' && (
                <>
                  {DIAS_SEMANA_EXTENSO[new Date(selectedDate + 'T12:00:00').getDay()]}, {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                </>
              )}
              {viewMode === 'motorista' && (
                <>
                  Escala de Motoristas: {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                </>
              )}
              {viewMode === 'lista' && 'Lista Completa de Fretes Programados'}
            </div>

            {/* Seletor Rápido de Data (Jump To Date) */}
            <div className="hidden lg:flex items-center gap-1.5 pl-2 border-l border-slate-200">
              <label htmlFor="jump-date-picker" className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Ir para:
              </label>
              <input
                id="jump-date-picker"
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  if (e.target.value) {
                    const newD = new Date(e.target.value + 'T12:00:00');
                    setSelectedDate(e.target.value);
                    setCurrentDate(newD);
                  }
                }}
                className="px-2 py-1 bg-slate-50 hover:bg-white border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-800 outline-none focus:ring-2 focus:ring-red-500 cursor-pointer transition"
                title="Pular diretamente para qualquer data"
              />
            </div>
          </div>

          {/* Seletores de Modos de Visão (Mês, Semana, Dia, Motorista, Lista) */}
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button
                onClick={() => setViewMode('mes')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  viewMode === 'mes'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <CalendarIcon className="w-3.5 h-3.5" />
                <span>Mês</span>
              </button>

              <button
                onClick={() => setViewMode('semana')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  viewMode === 'semana'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Semana</span>
              </button>

              <button
                onClick={() => setViewMode('dia')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  viewMode === 'dia'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                <span>Dia</span>
              </button>

              <button
                onClick={() => setViewMode('motorista')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  viewMode === 'motorista'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Truck className="w-3.5 h-3.5" />
                <span>Por Motorista</span>
              </button>

              <button
                onClick={() => setViewMode('lista')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  viewMode === 'lista'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <ListFilter className="w-3.5 h-3.5" />
                <span>Lista</span>
              </button>
            </div>

            <button
              onClick={() => setShowFiltersBar(!showFiltersBar)}
              className={`p-2 rounded-xl border text-xs font-bold transition flex items-center gap-1.5 ${
                showFiltersBar || selectedMotoristaFilter !== 'todos' || selectedStatusFilter !== 'todos' || selectedClienteFilter !== 'todos'
                  ? 'bg-red-50 text-red-600 border-red-200'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              title="Filtros avançados"
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Barra de Busca e Filtros Organizada */}
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar frete por OS #, cliente, motorista, placa ou endereço..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:ring-2 focus:ring-red-500 outline-none transition"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowFiltersBar(!showFiltersBar)}
              className={`px-3 py-2.5 rounded-xl border text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 ${
                showFiltersBar || selectedMotoristaFilter !== 'todos' || selectedStatusFilter !== 'todos' || selectedClienteFilter !== 'todos' || startDateFilter || endDateFilter
                  ? 'bg-red-50 text-red-600 border-red-200 shadow-2xs'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{showFiltersBar ? 'Ocultar Filtros' : 'Filtros'}</span>
              {showFiltersBar ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
              {(selectedMotoristaFilter !== 'todos' || selectedStatusFilter !== 'todos' || selectedClienteFilter !== 'todos' || startDateFilter || endDateFilter) && (
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              )}
            </button>
          </div>

          {/* Barra de Filtros Completa (Período de Data a Data + Motorista/Status/Cliente) */}
          {showFiltersBar && (
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3 animate-in fade-in duration-150">
              {/* 1. SEÇÃO DE DATA A DATA (PERÍODO) */}
              <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs space-y-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <CalendarRange className="w-4 h-4 text-red-600 shrink-0" />
                    <span className="text-xs font-bold text-slate-800">
                      Filtrar por Período (Data a Data)
                    </span>
                    {(startDateFilter || endDateFilter) && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                        Ativo
                      </span>
                    )}
                  </div>

                  {/* Atalhos Rápidos */}
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="text-slate-400 font-medium text-[10px] mr-0.5">Atalhos:</span>
                    <button
                      type="button"
                      onClick={() => aplicarPresetData('hoje')}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition cursor-pointer"
                    >
                      Hoje
                    </button>
                    <button
                      type="button"
                      onClick={() => aplicarPresetData('esta_semana')}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition cursor-pointer"
                    >
                      Esta Semana
                    </button>
                    <button
                      type="button"
                      onClick={() => aplicarPresetData('este_mes')}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition cursor-pointer"
                    >
                      Este Mês
                    </button>
                    <button
                      type="button"
                      onClick={() => aplicarPresetData('proximos_7')}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition cursor-pointer"
                    >
                      Próx. 7 Dias
                    </button>
                    <button
                      type="button"
                      onClick={() => aplicarPresetData('proximos_30')}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition cursor-pointer"
                    >
                      Próx. 30 Dias
                    </button>
                    {(startDateFilter || endDateFilter) && (
                      <button
                        type="button"
                        onClick={() => aplicarPresetData('limpar')}
                        className="px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 font-bold transition flex items-center gap-1 cursor-pointer"
                      >
                        <X className="w-3 h-3" /> Limpar Datas
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowFiltersBar(false)}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800 font-semibold transition flex items-center gap-1 cursor-pointer"
                      title="Ocultar painel de filtros"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                      <span>Ocultar</span>
                    </button>
                  </div>
                </div>

                {/* Inputs de Data Inicial e Final */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-slate-100">
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Data Inicial (De)
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={startDateFilter}
                        onChange={(e) => setStartDateFilter(e.target.value)}
                        className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2 bg-slate-50/50 text-slate-800 outline-none focus:bg-white focus:ring-2 focus:ring-red-500 font-medium cursor-pointer shadow-2xs"
                      />
                      {startDateFilter && (
                        <button
                          type="button"
                          onClick={() => setStartDateFilter('')}
                          className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                          title="Limpar data inicial"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                      Data Final (Até)
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={endDateFilter}
                        onChange={(e) => setEndDateFilter(e.target.value)}
                        className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2 bg-slate-50/50 text-slate-800 outline-none focus:bg-white focus:ring-2 focus:ring-red-500 font-medium cursor-pointer shadow-2xs"
                      />
                      {endDateFilter && (
                        <button
                          type="button"
                          onClick={() => setEndDateFilter('')}
                          className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                          title="Limpar data final"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. MENUS SUSPENSOS (MOTORISTA / STATUS / CLIENTE / LIMPAR TUDO) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                {/* Filtro Motorista */}
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Motorista
                  </label>
                  <select
                    value={selectedMotoristaFilter}
                    onChange={(e) => setSelectedMotoristaFilter(e.target.value)}
                    className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-800 outline-none focus:ring-2 focus:ring-red-500 font-medium cursor-pointer shadow-2xs"
                  >
                    <option value="todos">Todos Motoristas ({motoristasOptions.length})</option>
                    <option value="sem_motorista">⚠️ Sem Motorista</option>
                    {motoristasOptions.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nome} ({m.count} {m.count === 1 ? 'frete' : 'fretes'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filtro Status */}
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Status
                  </label>
                  <select
                    value={selectedStatusFilter}
                    onChange={(e) => setSelectedStatusFilter(e.target.value)}
                    className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-800 outline-none focus:ring-2 focus:ring-red-500 font-medium cursor-pointer shadow-2xs"
                  >
                    <option value="todos">Todos Status ({statusOptions.length})</option>
                    {statusOptions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.status}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Filtro Cliente */}
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Cliente
                  </label>
                  <select
                    value={selectedClienteFilter}
                    onChange={(e) => setSelectedClienteFilter(e.target.value)}
                    className="w-full text-xs border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-800 outline-none focus:ring-2 focus:ring-red-500 font-medium cursor-pointer shadow-2xs truncate"
                  >
                    <option value="todos">Todos Clientes ({clientesOptions.length})</option>
                    {clientesOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome} ({c.count} {c.count === 1 ? 'frete' : 'fretes'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ações / Limpar Todos os Filtros */}
                <div className="flex flex-col justify-end">
                  {(selectedMotoristaFilter !== 'todos' || selectedStatusFilter !== 'todos' || selectedClienteFilter !== 'todos' || startDateFilter || endDateFilter || searchTerm) ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedMotoristaFilter('todos');
                        setSelectedStatusFilter('todos');
                        setSelectedClienteFilter('todos');
                        setStartDateFilter('');
                        setEndDateFilter('');
                        setSearchTerm('');
                      }}
                      className="w-full px-3 py-2 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer h-[38px] shadow-2xs"
                    >
                      <X className="w-3.5 h-3.5" />
                      <span>Limpar Todos Filtros</span>
                    </button>
                  ) : (
                    <div className="hidden lg:flex items-center justify-center text-[11px] text-slate-400 font-medium h-[38px] border border-dashed border-slate-200 rounded-xl">
                      Sem filtros ativos
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 4. CONTEÚDO PRINCIPAL SEGUNDO O MODO SELECIONADO */}

      {/* MODO 1: CALENDÁRIO MENSAL */}
      {viewMode === 'mes' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Barra superior de controle de exibição do mês */}
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <CalendarDays className="w-4 h-4 text-red-600" />
                <span>Grade Mensal de Agendamentos</span>
              </div>

              {/* Alternador de densidade / expansão dos fretes */}
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-500 hidden sm:inline">Visualização nas células:</span>
                <div className="flex items-center bg-slate-200/80 p-0.5 rounded-lg border border-slate-300">
                  <button
                    type="button"
                    onClick={() => setMonthViewDensity('expandido')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      monthViewDensity === 'expandido'
                        ? 'bg-red-600 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    title="Exibir todos os fretes diretamente nas células do calendário"
                  >
                    <Maximize2 className="w-3 h-3" />
                    <span>Expandido (Ver Todos)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMonthViewDensity('compacto')}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      monthViewDensity === 'compacto'
                        ? 'bg-slate-900 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    title="Exibir até 3 fretes por célula com botão de expandir"
                  >
                    <Minimize2 className="w-3 h-3" />
                    <span>Compacto (Top 3)</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Cabeçalho dos dias da semana */}
            <div className="grid grid-cols-7 bg-slate-900 text-white text-center py-2.5 text-xs font-black tracking-wider uppercase">
              {DIAS_SEMANA_ABREV.map((dia, i) => (
                <div key={dia} className={i === 0 || i === 6 ? 'text-slate-400' : 'text-slate-200'}>
                  {dia}
                </div>
              ))}
            </div>

            {/* Grade de dias do mês */}
            <div className="grid grid-cols-7 auto-rows-fr divide-x divide-y divide-slate-100 bg-slate-50">
              {diasGradeMes.map((celula, idx) => {
                const temFretes = celula.fretes.length > 0;
                const isSelected = celula.dateStr === selectedDate;
                const totalValorDia = celula.fretes.reduce((acc, f) => acc + (f.total_os || 0), 0);
                const fretesParaExibir = monthViewDensity === 'expandido' ? celula.fretes : celula.fretes.slice(0, 3);
                const fretesOcultos = celula.fretes.length - fretesParaExibir.length;

                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedDate(celula.dateStr)}
                    className={`${
                      monthViewDensity === 'expandido'
                        ? 'min-h-[140px] lg:min-h-[165px]'
                        : 'min-h-[110px] lg:min-h-[125px]'
                    } p-2 flex flex-col transition-all cursor-pointer group ${
                      celula.isCurrentMonth ? 'bg-white' : 'bg-slate-50/70 opacity-60'
                    } ${isSelected ? 'ring-2 ring-red-600 ring-inset bg-red-50/20' : 'hover:bg-slate-50/90'}`}
                  >
                    {/* Topo da Célula: Número do Dia + Badges */}
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1">
                        <span
                          className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold transition ${
                            celula.isToday
                              ? 'bg-red-600 text-white font-black shadow-xs'
                              : isSelected
                              ? 'bg-slate-900 text-white'
                              : 'text-slate-700 group-hover:text-red-600'
                          }`}
                        >
                          {celula.dayNumber}
                        </span>
                      </div>

                      {temFretes && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDate(celula.dateStr);
                            setDayDetailsModal({
                              dateStr: celula.dateStr,
                              dayNumber: celula.dayNumber,
                              fretes: celula.fretes,
                            });
                          }}
                          className="flex items-center gap-1 text-[10px] font-mono font-bold px-1.5 py-0.2 rounded-full bg-slate-100 hover:bg-red-100 hover:text-red-700 text-slate-700 border border-slate-200 transition"
                          title="Clique para ver todos os fretes deste dia"
                        >
                          {celula.fretes.length} OS
                        </button>
                      )}
                    </div>

                    {/* Lista de fretes do dia */}
                    <div className={`space-y-1 flex-1 overflow-y-auto ${
                      monthViewDensity === 'expandido' ? 'max-h-[140px]' : 'max-h-[85px]'
                    } custom-scrollbar pr-0.5`}>
                      {fretesParaExibir.map((f) => {
                        const badgeStyle = getStatusBadgeStyle(f.status_cor, f.tipo_status);
                        return (
                          <div
                            key={f.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewOSId(f.id);
                            }}
                            className="px-1.5 py-0.5 rounded text-[10px] font-medium border flex items-center justify-between gap-1 truncate hover:opacity-85 transition cursor-pointer shadow-2xs"
                            style={badgeStyle}
                            title={`OS #${f.id} | ${f.cliente_nome} | ${f.motorista_nome} | ${f.retirada || 'Origem'} -> ${f.entrega || 'Destino'}`}
                          >
                            <span className="font-bold shrink-0">
                              {f.horario_inicio ? f.horario_inicio.slice(0, 5) : `#${f.id}`}
                            </span>
                            <span className="truncate flex-1">
                              {f.cliente_nome}
                            </span>
                          </div>
                        );
                      })}

                      {fretesOcultos > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDate(celula.dateStr);
                            setDayDetailsModal({
                              dateStr: celula.dateStr,
                              dayNumber: celula.dayNumber,
                              fretes: celula.fretes,
                            });
                          }}
                          className="w-full text-[9px] font-bold text-red-600 hover:text-white hover:bg-red-600 bg-red-50 border border-red-200 rounded py-0.5 px-1 transition text-center cursor-pointer flex items-center justify-center gap-1 shadow-2xs"
                          title="Clique para abrir e ver todos os fretes deste dia"
                        >
                          <Maximize2 className="w-2.5 h-2.5" />
                          <span>+{fretesOcultos} mais fretes</span>
                        </button>
                      )}
                    </div>

                    {/* Rodapé da célula se houver valor */}
                    {canViewPrices && totalValorDia > 0 && (
                      <div className="mt-auto pt-1 text-[9px] font-mono font-bold text-emerald-700 text-right truncate">
                        {formatBRL(totalValorDia)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Painel de Detalhes do Dia Selecionado no Mês */}
          {(() => {
            const fretesDoDiaSelecionado = fretesFiltrados.filter(f => f.data_atendimento === selectedDate);
            const dataObj = new Date(selectedDate + 'T12:00:00');
            const dataFormatada = dataObj.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
            const totalValorDia = fretesDoDiaSelecionado.reduce((acc, f) => acc + (f.total_os || 0), 0);

            return (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-bold">
                      <CalendarDays className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-slate-900 capitalize">
                        {dataFormatada}
                      </h3>
                      <p className="text-xs text-slate-500 font-medium">
                        {fretesDoDiaSelecionado.length} {fretesDoDiaSelecionado.length === 1 ? 'frete agendado' : 'fretes agendados'} nesta data
                        {canViewPrices && totalValorDia > 0 && ` • Total: ${formatBRL(totalValorDia)}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {canManageRoutes && fretesDoDiaSelecionado.length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleOpenRoteirizador(selectedDate)}
                        className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                      >
                        <Navigation className="w-3.5 h-3.5 text-red-400" />
                        <span>Roteirizar Dia</span>
                      </button>
                    )}

                    {canCreateOS && (
                      <button
                        type="button"
                        onClick={() => handleOpenNovaOS(selectedDate)}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        <span>Novo Frete</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Lista Completa dos Fretes do Dia Selecionado */}
                {fretesDoDiaSelecionado.length === 0 ? (
                  <div className="py-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                    <Clock className="w-6 h-6 mx-auto mb-1 opacity-40 text-slate-400" />
                    <p className="text-xs font-medium">Nenhum frete agendado para este dia.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {fretesDoDiaSelecionado.map((f) => {
                      const badgeStyle = getStatusBadgeStyle(f.status_cor, f.tipo_status);
                      return (
                        <div
                          key={f.id}
                          className="p-3 bg-slate-50 hover:bg-white border border-slate-200 hover:border-red-300 rounded-xl transition shadow-2xs hover:shadow-md space-y-2 group"
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-black text-slate-900 font-mono flex items-center gap-1">
                              <Clock className="w-3 h-3 text-red-600" />
                              {f.horario_inicio || 'Horário a definir'}
                            </span>
                            <span
                              className="px-2 py-0.5 rounded-md text-[10px] font-bold border truncate"
                              style={badgeStyle}
                            >
                              {f.status_nome}
                            </span>
                          </div>

                          <div>
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-mono font-bold text-red-600">
                                OS #{f.id} {f.pedido_id ? `(Ped #${f.pedido_id})` : ''}
                              </span>
                              {canViewPrices && (
                                <span className="text-xs font-mono font-bold text-slate-900">
                                  {formatBRL(f.total_os)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs font-bold text-slate-800 line-clamp-1">
                              {f.cliente_nome}
                            </p>
                          </div>

                          {/* Motorista e Veículo */}
                          <div className="text-[11px] text-slate-600 bg-white p-2 rounded-lg border border-slate-200/80 space-y-1">
                            <div className="flex items-center gap-1.5 truncate">
                              <User className="w-3 h-3 text-slate-400 shrink-0" />
                              <span className="truncate font-medium">{f.motorista_nome || 'Sem motorista'}</span>
                            </div>
                            {f.veiculo_placa && (
                              <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono">
                                <Truck className="w-3 h-3 text-slate-400 shrink-0" />
                                <span>{f.veiculo_placa} {f.veiculo_modelo ? `• ${f.veiculo_modelo}` : ''}</span>
                              </div>
                            )}
                          </div>

                          {/* Origem e Destino */}
                          {(f.retirada || f.entrega) && (
                            <div className="text-[10px] text-slate-500 space-y-0.5 pt-1">
                              {f.retirada && (
                                <p className="truncate flex items-center gap-1 text-slate-600">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                                  <span className="truncate">{f.retirada}</span>
                                </p>
                              )}
                              {f.entrega && (
                                <p className="truncate flex items-center gap-1 text-slate-600">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                                  <span className="truncate">{f.entrega}</span>
                                </p>
                              )}
                            </div>
                          )}

                          {/* Botão para abrir detalhes da OS */}
                          <div className="pt-2 border-t border-slate-100 flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => setViewOSId(f.id)}
                              className="px-2.5 py-1 bg-slate-900 hover:bg-red-600 text-white rounded-lg text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                            >
                              <Eye className="w-3 h-3" />
                              <span>Abrir OS</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* MODO 2: PROGRAMAÇÃO SEMANAL */}
      {viewMode === 'semana' && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {diasSemanaAtual.map((dia) => {
            const isSelected = dia.dateStr === selectedDate;
            const totalValorDia = dia.fretes.reduce((acc, f) => acc + (f.total_os || 0), 0);

            return (
              <div
                key={dia.dateStr}
                onClick={() => setSelectedDate(dia.dateStr)}
                className={`bg-white rounded-2xl border flex flex-col min-h-[480px] shadow-sm transition-all ${
                  isSelected
                    ? 'border-red-500 ring-2 ring-red-500/20 shadow-md'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                {/* Header do Dia da Semana */}
                <div
                  className={`p-3 rounded-t-2xl border-b flex items-center justify-between ${
                    dia.isToday
                      ? 'bg-red-600 text-white border-red-700'
                      : isSelected
                      ? 'bg-slate-900 text-white border-slate-800'
                      : 'bg-slate-100 text-slate-800 border-slate-200'
                  }`}
                >
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                      {dia.diaSemanaAbrev}
                    </p>
                    <p className="text-lg font-black leading-tight">
                      {dia.dayNumber}
                    </p>
                  </div>

                  <div className="text-right">
                    <span className="text-xs font-mono font-black px-2 py-0.5 rounded-full bg-black/20 text-white">
                      {dia.fretes.length} OS
                    </span>
                    {canViewPrices && totalValorDia > 0 && (
                      <p className="text-[10px] font-mono font-bold opacity-90 mt-0.5">
                        {formatBRL(totalValorDia)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Lista de Fretes da Coluna */}
                <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[580px] bg-slate-50/50">
                  {dia.fretes.length === 0 ? (
                    <div className="h-32 flex flex-col items-center justify-center text-center p-3 border border-dashed border-slate-200 rounded-xl text-slate-400">
                      <Clock className="w-5 h-5 mb-1 opacity-40" />
                      <p className="text-[11px]">Nenhum frete</p>
                      {canCreateOS && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenNovaOS(dia.dateStr);
                          }}
                          className="mt-2 text-[10px] font-bold text-red-600 hover:underline flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Agendar
                        </button>
                      )}
                    </div>
                  ) : (
                    dia.fretes.map((f) => {
                      const badgeStyle = getStatusBadgeStyle(f.status_cor, f.tipo_status);
                      return (
                        <div
                          key={f.id}
                          className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs hover:shadow-md transition space-y-2 group relative"
                        >
                          {/* Topo do Card: Horário e Status */}
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[11px] font-bold text-slate-800 font-mono flex items-center gap-1">
                              <Clock className="w-3 h-3 text-slate-400" />
                              {f.horario_inicio || '08:00'}
                            </span>
                            <span
                              className="px-2 py-0.5 rounded-md text-[9px] font-bold border truncate"
                              style={badgeStyle}
                            >
                              {f.status_nome}
                            </span>
                          </div>

                          {/* OS & Cliente */}
                          <div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-mono font-bold text-slate-400">
                                OS #{f.id}
                              </span>
                              {canViewPrices && (
                                <span className="text-[11px] font-mono font-bold text-slate-900">
                                  {formatBRL(f.total_os)}
                                </span>
                              )}
                            </div>
                            <h4 className="text-xs font-bold text-slate-900 truncate" title={f.cliente_nome}>
                              {f.cliente_nome}
                            </h4>
                          </div>

                          {/* Motorista & Veículo */}
                          <div className="text-[11px] text-slate-600 space-y-0.5">
                            <div className="flex items-center gap-1 font-medium truncate">
                              <User className="w-3 h-3 text-slate-400 shrink-0" />
                              <span className={f.motorista_id ? 'text-slate-800' : 'text-amber-600 font-bold'}>
                                {f.motorista_nome}
                              </span>
                            </div>
                            {f.veiculo_placa && (
                              <div className="flex items-center gap-1 font-mono text-[10px] text-slate-500">
                                <Truck className="w-3 h-3 text-slate-400 shrink-0" />
                                <span>{f.veiculo_placa} · {f.tipo_veiculo_nome}</span>
                              </div>
                            )}
                          </div>

                          {/* Origem -> Destino */}
                          {(f.retirada || f.entrega) && (
                            <div className="text-[10px] text-slate-500 space-y-0.5 pt-1 border-t border-slate-100">
                              {f.retirada && (
                                <p className="truncate" title={f.retirada}>
                                  📍 {f.retirada}
                                </p>
                              )}
                              {f.entrega && (
                                <p className="truncate font-medium text-slate-700" title={f.entrega}>
                                  🏁 {f.entrega}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Ações do Card */}
                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                            <button
                              onClick={() => setViewOSId(f.id)}
                              className="text-[10px] font-bold text-slate-600 hover:text-red-600 flex items-center gap-1 transition"
                            >
                              <Eye className="w-3 h-3" /> Ver Detalhes
                            </button>

                            {f.veiculos_id && (
                              <button
                                onClick={() => {
                                  setVeiculoParaTroca({
                                    id: f.veiculos_id,
                                    placa: f.veiculo_placa,
                                    motorista_id: f.motorista_id,
                                    motorista_nome: f.motorista_nome,
                                  });
                                  setIsTrocarMotoristaOpen(true);
                                }}
                                className="text-[10px] font-bold text-slate-500 hover:text-amber-600 flex items-center gap-1 transition"
                                title="Trocar ou atribuir motorista"
                              >
                                <UserCheck className="w-3 h-3" /> Motorista
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Botão de adicionar no rodapé */}
                {canCreateOS && (
                  <div className="p-2 border-t border-slate-100 bg-white rounded-b-2xl">
                    <button
                      onClick={() => handleOpenNovaOS(dia.dateStr)}
                      className="w-full py-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 text-xs font-bold rounded-lg border border-dashed border-slate-200 transition flex items-center justify-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Adicionar Frete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MODO 3: CRONOGRAMA DETALHADO DO DIA */}
      {viewMode === 'dia' && (
        <div className="space-y-4">
          <div className="bg-slate-900 text-white p-5 rounded-2xl shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-amber-400 uppercase tracking-widest">
                Programação Operacional Diária
              </p>
              <h2 className="text-2xl font-black tracking-tight mt-0.5 capitalize">
                {DIAS_SEMANA_EXTENSO[new Date(selectedDate + 'T12:00:00').getDay()]}, {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}
              </h2>
              <p className="text-xs text-slate-400 mt-1">
                {fretesDoDiaSelecionado.length} fretes programados · {canViewPrices && `Faturamento Previsto: ${formatBRL(fretesDoDiaSelecionado.reduce((acc, f) => acc + (f.total_os || 0), 0))}`}
              </p>
            </div>

            <div className="flex items-center gap-2">
              {canCreateOS && (
                <button
                  onClick={() => handleOpenNovaOS(selectedDate)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-md transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> Novo Frete neste Dia
                </button>
              )}
              {canManageRoutes && (
                <button
                  onClick={() => handleOpenRoteirizador(selectedDate)}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-bold rounded-xl shadow-md transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Navigation className="w-4 h-4" /> Sequenciar Rota do Dia
                </button>
              )}
            </div>
          </div>

          {fretesDoDiaSelecionado.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center space-y-3">
              <Truck className="w-12 h-12 text-slate-300 mx-auto" />
              <h3 className="text-base font-bold text-slate-800">
                Nenhum frete programado para este dia
              </h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Utilize os botões de ação para cadastrar novas ordens de serviço ou avance a data para consultar outros dias.
              </p>
              {canCreateOS && (
                <button
                  onClick={() => handleOpenNovaOS(selectedDate)}
                  className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl shadow-md inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Agendar Frete Agora
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fretesDoDiaSelecionado.map((f, i) => {
                const badgeStyle = getStatusBadgeStyle(f.status_cor, f.tipo_status);
                return (
                  <div
                    key={f.id}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition p-5 flex flex-col justify-between space-y-4"
                  >
                    {/* Topo do Frete */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-lg bg-slate-900 text-white font-mono font-bold text-xs flex items-center justify-center">
                            #{i + 1}
                          </span>
                          <span className="text-xs font-mono font-black text-slate-900">
                            OS #{f.id}
                          </span>
                        </div>
                        <span
                          className="px-2.5 py-0.5 rounded-full text-xs font-bold border shadow-2xs"
                          style={badgeStyle}
                        >
                          {f.status_nome}
                        </span>
                      </div>

                      {/* Horário & Solicitante */}
                      <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500 font-medium">Horário Previsto:</span>
                          <span className="font-bold text-slate-900 font-mono flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-red-600" />
                            {f.horario_inicio ? `${f.horario_inicio} ${f.horario_final ? `às ${f.horario_final}` : ''}` : 'Horário Comercial'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500 font-medium">Cliente / Solicitante:</span>
                          <span className="font-bold text-slate-900 truncate max-w-[170px]" title={f.cliente_nome}>
                            {f.cliente_nome}
                          </span>
                        </div>
                      </div>

                      {/* Motorista & Veículo */}
                      <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-200/60 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-amber-900 font-medium">Motorista Escalado:</span>
                          <span className={`font-bold ${f.motorista_id ? 'text-amber-950' : 'text-red-600 font-black'}`}>
                            {f.motorista_nome}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs font-mono">
                          <span className="text-amber-800">Veículo & Placa:</span>
                          <span className="font-bold text-amber-950">
                            {f.veiculo_placa ? `${f.veiculo_placa} (${f.tipo_veiculo_nome})` : 'A definir'}
                          </span>
                        </div>
                        {f.quantidade_ajudante ? (
                          <div className="text-[11px] text-amber-800 flex items-center gap-1 pt-1">
                            <User className="w-3 h-3" /> {f.quantidade_ajudante} ajudante(s) escalado(s)
                          </div>
                        ) : null}
                      </div>

                      {/* Rota */}
                      <div className="space-y-1.5 text-xs">
                        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                          <p className="text-[10px] font-bold uppercase text-slate-400">1. Retirada / Coleta</p>
                          <p className="text-slate-800 font-medium text-xs truncate" title={f.retirada}>
                            📍 {f.retirada || 'Endereço de coleta a confirmar'}
                          </p>
                        </div>
                        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                          <p className="text-[10px] font-bold uppercase text-slate-400">2. Entrega / Descarga</p>
                          <p className="text-slate-800 font-medium text-xs truncate" title={f.entrega}>
                            🏁 {f.entrega || 'Endereço de entrega a confirmar'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Valores e Ações */}
                    <div className="pt-3 border-t border-slate-100 space-y-3">
                      {canViewPrices && (
                        <div className="flex items-center justify-between text-xs font-mono">
                          <span className="text-slate-500">Valor Frete:</span>
                          <span className="font-black text-slate-900 text-sm">{formatBRL(f.total_os)}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewOSId(f.id)}
                          className="flex-1 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition flex items-center justify-center gap-1.5"
                        >
                          <Eye className="w-3.5 h-3.5" /> Detalhes da OS
                        </button>
                        {f.veiculos_id && (
                          <button
                            onClick={() => {
                              setVeiculoParaTroca({
                                id: f.veiculos_id,
                                placa: f.veiculo_placa,
                                motorista_id: f.motorista_id,
                                motorista_nome: f.motorista_nome,
                              });
                              setIsTrocarMotoristaOpen(true);
                            }}
                            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                            title="Reatribuir motorista"
                          >
                            <UserCheck className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MODO 4: QUADRO POR MOTORISTA / SWIMLANES */}
      {viewMode === 'motorista' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-200">
            <div>
              <h3 className="font-black text-slate-900 text-base">
                Escala de Motoristas para {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}
              </h3>
              <p className="text-xs text-slate-500">
                Visualize a carga de trabalho atribuída a cada motorista para balancear a frota.
              </p>
            </div>
            <span className="px-3 py-1 bg-slate-100 rounded-xl text-xs font-mono font-bold text-slate-800">
              {motoristasSwimlanes.length} colunas ativas
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {motoristasSwimlanes.map((col) => (
              <div
                key={col.key}
                className={`rounded-2xl border flex flex-col min-h-[400px] shadow-sm ${
                  col.key === 'sem_motorista'
                    ? 'bg-rose-50/50 border-rose-200'
                    : 'bg-white border-slate-200'
                }`}
              >
                {/* Header da Coluna do Motorista */}
                <div
                  className={`p-3.5 rounded-t-2xl border-b flex items-center justify-between ${
                    col.key === 'sem_motorista'
                      ? 'bg-rose-600 text-white border-rose-700'
                      : 'bg-slate-900 text-white border-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <User className="w-4 h-4 shrink-0" />
                    <span className="font-bold text-xs truncate" title={col.nome}>
                      {col.nome}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-black/30 text-white shrink-0">
                    {col.fretes.length} OS
                  </span>
                </div>

                {/* Lista de Fretes do Motorista */}
                <div className="p-3 space-y-3 flex-1 overflow-y-auto max-h-[600px]">
                  {col.fretes.length === 0 ? (
                    <div className="h-32 flex items-center justify-center text-xs text-slate-400 italic">
                      Nenhum frete escalado
                    </div>
                  ) : (
                    col.fretes.map((f) => {
                      const badgeStyle = getStatusBadgeStyle(f.status_cor, f.tipo_status);
                      return (
                        <div
                          key={f.id}
                          className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-mono font-bold text-slate-900">
                              OS #{f.id}
                            </span>
                            <span
                              className="px-2 py-0.5 rounded text-[9px] font-bold border"
                              style={badgeStyle}
                            >
                              {f.status_nome}
                            </span>
                          </div>

                          <p className="text-xs font-bold text-slate-800 truncate" title={f.cliente_nome}>
                            {f.cliente_nome}
                          </p>

                          <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {f.horario_inicio || '08:00'} · {f.veiculo_placa || 'Sem placa'}
                          </div>

                          <div className="text-[10px] text-slate-500 space-y-0.5 pt-1 border-t border-slate-100">
                            <p className="truncate">📍 {f.retirada}</p>
                            <p className="truncate font-medium text-slate-700">🏁 {f.entrega}</p>
                          </div>

                          <button
                            onClick={() => setViewOSId(f.id)}
                            className="w-full py-1.5 text-center text-xs font-bold text-slate-700 hover:text-red-600 bg-slate-50 hover:bg-red-50 rounded-lg transition"
                          >
                            Ver Detalhes
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODO 5: LISTA COMPACTA DE FRETES */}
      {viewMode === 'lista' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <ListFilter className="w-4 h-4 text-red-600" />
              Todas as Ordens de Serviço ({fretesFiltrados.length} encontradas)
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-900 text-white font-mono uppercase text-[10px]">
                <tr>
                  <th className="py-3 px-4">OS #</th>
                  <th className="py-3 px-4">Data Atendimento</th>
                  <th className="py-3 px-4">Horário</th>
                  <th className="py-3 px-4">Cliente</th>
                  <th className="py-3 px-4">Motorista</th>
                  <th className="py-3 px-4">Veículo</th>
                  <th className="py-3 px-4">Retirada → Entrega</th>
                  <th className="py-3 px-4">Status</th>
                  {canViewPrices && <th className="py-3 px-4 text-right">Valor Frete</th>}
                  <th className="py-3 px-4 text-center">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fretesFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-slate-400">
                      Nenhum frete encontrado com os filtros aplicados.
                    </td>
                  </tr>
                ) : (
                  fretesFiltrados.map((f) => {
                    const badgeStyle = getStatusBadgeStyle(f.status_cor, f.tipo_status);
                    return (
                      <tr key={f.id} className="hover:bg-slate-50 transition">
                        <td className="py-3 px-4 font-mono font-bold text-slate-900">
                          #{f.id}
                        </td>
                        <td className="py-3 px-4 font-mono">
                          {new Date(f.data_atendimento + 'T12:00:00').toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3 px-4 font-mono text-slate-600">
                          {f.horario_inicio || '08:00'}
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-800 max-w-[180px] truncate" title={f.cliente_nome}>
                          {f.cliente_nome}
                        </td>
                        <td className="py-3 px-4 text-slate-700 max-w-[140px] truncate" title={f.motorista_nome}>
                          {f.motorista_nome}
                        </td>
                        <td className="py-3 px-4 font-mono text-slate-600">
                          {f.veiculo_placa || '—'}
                        </td>
                        <td className="py-3 px-4 max-w-[220px]">
                          <p className="truncate text-slate-500">📍 {f.retirada}</p>
                          <p className="truncate font-medium text-slate-800">🏁 {f.entrega}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className="px-2.5 py-0.5 rounded-full text-[10px] font-bold border"
                            style={badgeStyle}
                          >
                            {f.status_nome}
                          </span>
                        </td>
                        {canViewPrices && (
                          <td className="py-3 px-4 text-right font-mono font-bold text-slate-900">
                            {formatBRL(f.total_os)}
                          </td>
                        )}
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => setViewOSId(f.id)}
                            className="p-1.5 rounded-lg bg-slate-100 hover:bg-red-50 text-slate-700 hover:text-red-600 transition"
                            title="Ver Detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 5. MODAIS INTEGRADOS */}

      {/* Modal Detalhes Completos do Dia (Ao clicar em +X mais fretes ou no badge) */}
      {dayDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header do Modal */}
            <div className="p-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center font-black text-lg shadow-md">
                  {dayDetailsModal.dayNumber}
                </div>
                <div>
                  <h2 className="text-base font-black capitalize">
                    {new Date(dayDetailsModal.dateStr + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                  </h2>
                  <p className="text-xs text-slate-400 font-medium">
                    {dayDetailsModal.fretes.length} {dayDetailsModal.fretes.length === 1 ? 'frete programado' : 'fretes programados'}
                    {canViewPrices && ` • Total: ${formatBRL(dayDetailsModal.fretes.reduce((acc, f) => acc + (f.total_os || 0), 0))}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {canCreateOS && (
                  <button
                    type="button"
                    onClick={() => {
                      const dt = dayDetailsModal.dateStr;
                      setDayDetailsModal(null);
                      handleOpenNovaOS(dt);
                    }}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Novo Frete</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setDayDetailsModal(null)}
                  className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Corpo do Modal: Lista Completa dos Fretes do Dia */}
            <div className="p-4 overflow-y-auto space-y-3 bg-slate-50 flex-1">
              {dayDetailsModal.fretes.map((f, i) => {
                const badgeStyle = getStatusBadgeStyle(f.status_cor, f.tipo_status);
                return (
                  <div
                    key={f.id}
                    className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-xs hover:border-red-300 transition space-y-2.5"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-700 font-mono font-bold text-xs flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="text-xs font-black text-slate-900 font-mono flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-red-600" />
                          {f.horario_inicio || '08:00'}
                          {f.horario_final && ` - ${f.horario_final}`}
                        </span>
                        <span className="text-xs font-mono font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-100">
                          OS #{f.id} {f.pedido_id ? `(Ped #${f.pedido_id})` : ''}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className="px-2.5 py-0.5 rounded-full text-[11px] font-bold border"
                          style={badgeStyle}
                        >
                          {f.status_nome}
                        </span>
                        {canViewPrices && (
                          <span className="text-sm font-mono font-black text-slate-900">
                            {formatBRL(f.total_os)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Cliente e Motorista */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Cliente</span>
                        <span className="font-bold text-slate-800 line-clamp-1">{f.cliente_nome}</span>
                      </div>

                      <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Motorista & Veículo</span>
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-bold text-slate-800 truncate">{f.motorista_nome || 'Sem motorista'}</span>
                          {f.veiculo_placa && (
                            <span className="font-mono text-[11px] text-slate-600 bg-white px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                              {f.veiculo_placa}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Origem e Destino */}
                    {(f.retirada || f.entrega) && (
                      <div className="p-2 bg-slate-50 rounded-lg border border-slate-100 text-xs space-y-1">
                        {f.retirada && (
                          <div className="flex items-start gap-1.5 text-slate-700">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1 shrink-0" />
                            <span className="font-medium text-slate-500 text-[11px] shrink-0">Coleta:</span>
                            <span className="font-semibold">{f.retirada}</span>
                          </div>
                        )}
                        {f.entrega && (
                          <div className="flex items-start gap-1.5 text-slate-700">
                            <span className="w-2 h-2 rounded-full bg-red-500 mt-1 shrink-0" />
                            <span className="font-medium text-slate-500 text-[11px] shrink-0">Entrega:</span>
                            <span className="font-semibold text-slate-900">{f.entrega}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Ações */}
                    <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={() => {
                          setViewOSId(f.id);
                        }}
                        className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                      >
                        <Eye className="w-3.5 h-3.5 text-red-400" />
                        <span>Visualizar Detalhes</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer do Modal */}
            <div className="p-3 bg-white border-t border-slate-200 flex items-center justify-between">
              {canManageRoutes && dayDetailsModal.fretes.length > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    const dt = dayDetailsModal.dateStr;
                    setDayDetailsModal(null);
                    handleOpenRoteirizador(dt);
                  }}
                  className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <Navigation className="w-3.5 h-3.5 text-red-400" />
                  <span>Roteirizar Fretes deste Dia</span>
                </button>
              ) : <div />}

              <button
                type="button"
                onClick={() => setDayDetailsModal(null)}
                className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg text-xs font-bold transition cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Visualizar OS */}
      {viewOSId && (
        <VisualizarOSModal
          osId={viewOSId}
          onClose={() => setViewOSId(null)}
        />
      )}

      {/* Modal Cadastro/Novo Frete */}
      {isCadastroOpen && (
        <CadastroOSModal
          isOpen={isCadastroOpen}
          initialDate={cadastroDate}
          onClose={() => setIsCadastroOpen(false)}
          onSaved={() => {
            setIsCadastroOpen(false);
            carregarDadosAgenda();
          }}
        />
      )}

      {/* Modal Criador e Sequenciador de Roteiro */}
      {isRoteiroOpen && (
        <CriadorRoteiroModal
          isOpen={isRoteiroOpen}
          initialDate={roteiroDate}
          onClose={() => setIsRoteiroOpen(false)}
          onRoteiroSalvo={() => {
            setIsRoteiroOpen(false);
            carregarDadosAgenda();
          }}
        />
      )}

      {/* Modal Trocar Motorista / Veículo */}
      {isTrocarMotoristaOpen && (
        <TrocarMotoristaVeiculoModal
          isOpen={isTrocarMotoristaOpen}
          onClose={() => setIsTrocarMotoristaOpen(false)}
          veiculo={veiculoParaTroca}
          veiculosOptions={veiculosList}
          pessoasOptions={motoristasList}
          onMotoristaAtualizado={() => {
            setIsTrocarMotoristaOpen(false);
            carregarDadosAgenda();
          }}
        />
      )}
    </div>
  );
}
